import { useState, useEffect } from 'react';
import { type MoodEntry, type MoodLevel, MOOD_CONFIG, formatDate } from '../api/mockData';
import { MoodSelector } from './MoodSelector';
import { NoteInput } from './NoteInput';

interface DayDetailProps {
  date: string;
  entry?: MoodEntry;
  onSave: (entry: MoodEntry) => void;
  onClose: () => void;
}

export function DayDetail({ date, entry, onSave, onClose }: DayDetailProps) {
  const [mood, setMood] = useState<MoodLevel | undefined>(entry?.mood);
  const [note, setNote] = useState(entry?.note || '');

  useEffect(() => {
    setMood(entry?.mood);
    setNote(entry?.note || '');
  }, [date, entry]);

  const dateObj = new Date(date + 'T12:00:00');
  const today = formatDate(new Date());
  const isToday = date === today;

  const dayLabel = isToday
    ? 'Today'
    : dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const handleSave = () => {
    if (mood) {
      onSave({ date, mood, note: note.trim() || undefined });
    }
  };

  const hasChanges = mood !== entry?.mood || (note.trim() || undefined) !== entry?.note;
  const moodConfig = mood ? MOOD_CONFIG[mood] : null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="animate-fade-up"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(58, 46, 40, 0.3)',
          backdropFilter: 'blur(4px)',
          animationDuration: '0.2s',
        }}
      />
      {/* Panel */}
      <div
        className="animate-fade-up"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '480px',
          background: 'white',
          borderRadius: '24px 24px 0 0',
          padding: '8px 20px 32px',
          boxShadow: '0 -8px 40px rgba(58, 46, 40, 0.12)',
          maxHeight: '85vh',
          overflowY: 'auto',
          animationDuration: '0.3s',
        }}
      >
        {/* Drag handle */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '8px 0 16px',
        }}>
          <div style={{
            width: 40,
            height: 4,
            borderRadius: 2,
            background: 'var(--journal-line)',
          }} />
        </div>

        {/* Date title */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.4rem',
            fontWeight: 700,
            color: 'var(--journal-ink)',
            margin: 0,
          }}>
            {dayLabel}
          </h3>
          {!isToday && (
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.8rem',
              color: 'var(--journal-ink-faint)',
              margin: '4px 0 0',
            }}>
              {date}
            </p>
          )}
        </div>

        {/* Current mood display */}
        {moodConfig && (
          <div style={{
            textAlign: 'center',
            padding: '16px',
            margin: '0 0 20px',
            borderRadius: '16px',
            background: moodConfig.bgColor,
            transition: 'all 0.3s ease',
          }}>
            <span style={{ fontSize: '3rem', display: 'block', marginBottom: '6px' }}>
              {moodConfig.emoji}
            </span>
            <span style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1rem',
              fontWeight: 600,
              color: moodConfig.deepColor,
            }}>
              Feeling {moodConfig.label}
            </span>
          </div>
        )}

        {/* Mood selector */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: 'var(--journal-ink-light)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: '8px',
          }}>
            {entry ? 'Change mood' : 'How are you feeling?'}
          </p>
          <MoodSelector selected={mood} onSelect={setMood} />
        </div>

        {/* Note input */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: 'var(--journal-ink-light)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: '8px',
          }}>
            Add a note
          </p>
          <NoteInput value={note} onChange={setNote} />
        </div>

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={!mood || !hasChanges}
          style={{
            width: '100%',
            padding: '14px 24px',
            borderRadius: '14px',
            border: 'none',
            background: mood && hasChanges
              ? 'var(--journal-accent)'
              : 'var(--journal-line)',
            color: mood && hasChanges ? 'white' : 'var(--journal-ink-faint)',
            fontFamily: 'var(--font-body)',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: mood && hasChanges ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s',
            letterSpacing: '0.01em',
          }}
          onMouseEnter={(e) => {
            if (mood && hasChanges) {
              e.currentTarget.style.background = '#b0522e';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(196, 96, 58, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = mood && hasChanges ? 'var(--journal-accent)' : 'var(--journal-line)';
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {entry ? 'Update Entry' : 'Save Entry'}
        </button>
      </div>
    </div>
  );
}
